"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql, ResultOf } from "gql.tada";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import { EDIT_DASHBOARD_ITEM_NAME } from "./constants";
import type { EditDashboardItemInput } from "./schema";

const EditDashboardItemConfirmQuery = graphql(`
  query EditDashboardItemConfirmQuery($id: ID!) {
    node(id: $id) {
      __typename
      ... on DashboardItemDivider {
        id
        name
        description
        type
        dashboard {
          id
          name
        }
      }
      ... on DashboardItemMetricWithTimeSeries {
        id
        name
        description
        type
        featureID
        dashboard {
          id
          name
        }
      }
      ... on DashboardItemPageTransitionRate {
        id
        name
        description
        type
        transitionFeatureID: featureID
        dashboard {
          id
          name
        }
      }
    }
  }
`);

const FeatureNameQuery = graphql(`
  query DashboardItemFeatureName($id: ID!) {
    node(id: $id) {
      __typename
      ... on IndexWorker {
        id
        name
      }
      ... on SearchAnalyticsView {
        id
        name
      }
      ... on UserAnalyticsView {
        id
        name
      }
      ... on SearchAnalytics {
        id
      }
      ... on UserAnalytics {
        id
      }
    }
  }
`);

type FeatureNameResult = ResultOf<typeof FeatureNameQuery>;

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

type FeatureLabel = {
  label: string | null;
  isAll: boolean;
};

function resolveFeatureLabel(
  result: FeatureNameResult | undefined,
  id: string | null | undefined,
): FeatureLabel {
  const node = result?.node;
  if (
    node?.__typename === "IndexWorker" ||
    node?.__typename === "SearchAnalyticsView" ||
    node?.__typename === "UserAnalyticsView"
  ) {
    return { label: node.name ?? id ?? null, isAll: false };
  }
  if (
    node?.__typename === "SearchAnalytics" ||
    node?.__typename === "UserAnalytics"
  ) {
    return { label: null, isAll: true };
  }
  return { label: id ?? null, isAll: false };
}

/**
 * Dashboard item 編集時の確認 UI を表示する。
 * 既存アイテムの情報と提案された変更内容を並べて確認できる。
 */
export function EditDashboardItemConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const input = (part.input ?? {}) as Partial<EditDashboardItemInput>;
  const itemId = input.id ?? "";

  const isTarget =
    isToolUIPart(part) &&
    getToolName(part) === EDIT_DASHBOARD_ITEM_NAME &&
    part.state === "input-available";

  const newName = typeof input.name === "string" ? input.name : undefined;
  const descriptionProvided = "description" in input;
  const newDescription =
    input.description === null
      ? null
      : typeof input.description === "string"
        ? input.description
        : undefined;
  const featureProvided = "featureId" in input;
  const newFeatureId =
    featureProvided && typeof input.featureId === "string"
      ? input.featureId
      : featureProvided && input.featureId === null
        ? null
        : undefined;

  const [{ data, fetching }] = useQuery({
    query: EditDashboardItemConfirmQuery,
    variables: { id: itemId },
    pause: !isTarget || !itemId,
  });

  const item =
    data?.node?.__typename === "DashboardItemMetricWithTimeSeries" ||
    data?.node?.__typename === "DashboardItemPageTransitionRate" ||
    data?.node?.__typename === "DashboardItemDivider"
      ? data.node
      : null;

  const currentFeatureId =
    item?.__typename === "DashboardItemPageTransitionRate"
      ? item.transitionFeatureID
      : item?.__typename === "DashboardItemMetricWithTimeSeries"
        ? (item.featureID ?? null)
        : null;

  const [{ data: currentFeatureData, fetching: currentFeatureLoading }] =
    useQuery({
      query: FeatureNameQuery,
      variables: { id: currentFeatureId ?? "" },
      pause: !isTarget || !currentFeatureId,
    });

  const [{ data: newFeatureData, fetching: newFeatureLoading }] = useQuery({
    query: FeatureNameQuery,
    variables: { id: newFeatureId ?? "" },
    pause: !isTarget || !newFeatureId,
  });

  if (!isTarget) return null;

  const pending = fetching || !item;
  const dashboardName = item?.dashboard?.name ?? null;

  const currentFeatureLabel = resolveFeatureLabel(
    currentFeatureData,
    currentFeatureId,
  );
  const nextFeatureLabel = resolveFeatureLabel(newFeatureData, newFeatureId);

  const nameChanged =
    typeof newName === "string" &&
    item?.name &&
    newName.trim() !== item.name.trim();
  const descriptionChanged =
    descriptionProvided && (newDescription ?? "") !== (item?.description ?? "");
  const featureChanged = featureProvided && newFeatureId !== currentFeatureId;

  const showFeatureRow =
    item?.__typename === "DashboardItemMetricWithTimeSeries" ||
    item?.__typename === "DashboardItemPageTransitionRate" ||
    featureProvided;

  const renderFeatureValue = (
    label: FeatureLabel,
    id: string | null | undefined,
  ) => {
    if (label.isAll) return <Trans id="default-view.name">All</Trans>;
    if (label.label) return label.label;
    if (id === null) return <Trans>None</Trans>;
    if (id) return id;
    return <Trans>Unknown</Trans>;
  };

  return (
    <div className="p-4 text-sm space-y-4">
      <div>
        <div className="mb-1 font-medium">
          <Trans>Do you want to update this dashboard item?</Trans>
        </div>
        <div className="text-muted-foreground">
          <Trans>Review the changes and confirm to apply them.</Trans>
        </div>
      </div>

      <ul className="list-disc space-y-2 pl-5">
        <li>
          <Trans>Dashboard</Trans>:{" "}
          {pending ? (
            <Skeleton className="inline-block h-4 w-24 align-middle" />
          ) : (
            <span className="font-medium">
              {dashboardName ?? <Trans>Unknown</Trans>}
            </span>
          )}
        </li>

        <li>
          <Trans>Item name</Trans>:{" "}
          {pending ? (
            <Skeleton className="inline-block h-4 w-32 align-middle" />
          ) : (
            <>
              <span>{item?.name ?? "-"}</span>
              {typeof newName === "string" ? (
                nameChanged ? (
                  <span className="ml-2 font-semibold">→ {newName}</span>
                ) : (
                  <span className="ml-2 text-muted-foreground">
                    (<Trans>No changes</Trans>)
                  </span>
                )
              ) : (
                <span className="ml-2 text-muted-foreground">
                  (<Trans>No changes</Trans>)
                </span>
              )}
            </>
          )}
        </li>

        <li>
          <div className="mb-1">
            <Trans>Description</Trans>
            {!descriptionChanged && !pending ? (
              <span className="ml-2 text-muted-foreground">
                (<Trans>No changes</Trans>)
              </span>
            ) : null}
          </div>
          {pending ? (
            <Skeleton className="h-10 w-full max-w-md" />
          ) : descriptionChanged ? (
            <div className="rounded border bg-muted/30 p-2 text-xs">
              <div className="mb-1">
                <span className="mr-2">
                  <Trans>Current:</Trans>
                </span>
                <span>{item?.description ?? "-"}</span>
              </div>
              <div>
                <span className="mr-2">
                  <Trans>New:</Trans>
                </span>
                <span className="font-semibold">
                  {newDescription === null ? (
                    <Trans>Clear description</Trans>
                  ) : (
                    newDescription
                  )}
                </span>
              </div>
            </div>
          ) : null}
        </li>

        {showFeatureRow ? (
          <li>
            <div className="flex flex-wrap items-center gap-2">
              <span>
                <Trans>Linked feature</Trans>:
              </span>
              {pending ||
              currentFeatureLoading ||
              (featureProvided && newFeatureLoading && !!newFeatureId) ? (
                <Skeleton className="h-4 w-28" />
              ) : (
                <>
                  <span className="font-mono text-xs">
                    {renderFeatureValue(currentFeatureLabel, currentFeatureId)}
                  </span>
                  {featureProvided ? (
                    featureChanged ? (
                      <>
                        <span>→</span>
                        <span className="font-mono text-xs font-semibold">
                          {renderFeatureValue(nextFeatureLabel, newFeatureId)}
                        </span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">
                        (<Trans>No changes</Trans>)
                      </span>
                    )
                  ) : (
                    <span className="text-muted-foreground">
                      (<Trans>No changes</Trans>)
                    </span>
                  )}
                </>
              )}
            </div>
          </li>
        ) : null}
      </ul>

      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Update</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
